import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import type {
  CSSProperties,
  MouseEventHandler,
  PointerEventHandler,
  PropsWithChildren,
  ReactElement,
  ReactNode,
} from 'react';

import DeleteIcon from 'src/shared/assets/icons/delete.svg';
import Card from 'src/shared/components/card/card';
import { useOnClick } from 'src/shared/hooks/use-on-click';
import type { LoadingField } from 'src/shared/utils/loading-field';

import styles from './pad-card.module.scss';

interface Props {
  title: ReactElement;
  attributes?: (string | ReactNode | LoadingField)[] | null;
  extra?: ReactNode;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  isCompactView?: boolean;
  onPointerDown?: PointerEventHandler<HTMLDivElement>;
  onPointerUp?: PointerEventHandler<HTMLDivElement>;
  onMouseOver?: MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: MouseEventHandler<HTMLDivElement>;
  style?: CSSProperties;
  onDelete?(): void;
}

export const PadCard = observer(function PadCard({
  title,
  extra,
  className,
  attributes,
  onPointerDown,
  onPointerUp,
  onMouseLeave,
  onMouseOver,
  isCompactView,
  contentClassName,
  headerClassName,
  children,
  style,
  onDelete,
}: PropsWithChildren<Props>) {
  const deleteButtonListeners = useOnClick(() => onDelete?.());

  const header = (
    <div className={styles.cardHeaderWrapper}>
      <div className={clsx(headerClassName, styles.cardHeader, isCompactView && styles.cardHeader__compact)}>
        {onDelete && (
          <button className={styles.cardHeaderDeleteButton} {...deleteButtonListeners}>
            <DeleteIcon className={styles.cardHeaderDeleteButtonIcon} />
          </button>
        )}
        <div className={styles.cardHeaderTitle}>{attributes ? <h3>{attributes?.join(' / ')}</h3> : title}</div>

        {!!extra && extra}
      </div>
    </div>
  );

  return (
    <Card
      isCompactView={isCompactView}
      header={header}
      className={clsx(className, styles.card, isCompactView && styles.card__compact)}
      contentClassName={contentClassName}
      style={style}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onMouseLeave={onMouseLeave}
      onMouseOver={onMouseOver}
      isInteractive
    >
      {children}
    </Card>
  );
});
